import _ from 'lodash'
import type {CreateExtArgs} from '@wix/document-manager-core'
import type {CompRef} from '@wix/document-services-types'
import type {StructureExtensionAPI} from '../../structure'
import {guidUtils, displayedOnlyStructureUtil} from '@wix/santa-core-utils'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {AddItemOptions, DataModelExtensionAPI} from '../../dataModel/dataModel'
import {DATA_TYPES} from '../../../constants/constants'

//TODO refactor the functions to handle not only 'richText' prop in builder
// as linkList can be in other props that aren't necessarily called 'richText'
const updateComponentData = (
    {extensionAPI}: CreateExtArgs,
    compPointer: CompRef,
    fixedText: string,
    newLinks: string[],
    languageCode?: string
) => {
    const {components, dataModel} = extensionAPI as DataModelExtensionAPI & StructureExtensionAPI

    const compData = dataModel.components.getItem(compPointer, DATA_TYPES.data, languageCode, true)
    const options: AddItemOptions = {}

    if (compData.richText) {
        compData.richText.text = fixedText
        compData.richText.linkList = newLinks
        options.elementType = components.getComponentType(compPointer)
    } else {
        compData.text = fixedText
        compData.linkList = newLinks
    }

    dataModel.components.addItem(compPointer, DATA_TYPES.data, compData, languageCode, options)
}

const createTextLinkReferences = (
    createExtArgs: CreateExtArgs,
    compPointer: CompRef,
    newLinkList: any[],
    text: string,
    languageCode?: string
) => {
    let textToFix: string = _.clone(text)
    // eslint-disable-next-line lodash/prefer-filter
    _.forEach(newLinkList, linkData => {
        if (linkData.id) {
            const newId = guidUtils.getUniqueId('link', '-', {bucket: 'links'})
            textToFix = textToFix.replace(`#${linkData.id}`, `#${newId}`)
            linkData.id = newId
        }
    })

    updateComponentData(createExtArgs, compPointer, textToFix, newLinkList, languageCode)
}

type LinkList = Record<string, any>

interface ComponentSerializedCustomData {
    originalCompId?: string
    linkList?: LinkList
    translations?: {
        linkList?: Record<string, LinkList>
    }
}

export const duplicateLinksDataItems = (createExtArgs: CreateExtArgs, {compToAddPointer, componentDefinition}: any) => {
    const {pointers} = createExtArgs

    if (!componentDefinition.custom) {
        return {success: true, description: 'Duplicated text links data items'}
    }

    const {linkList, originalCompId, translations} = componentDefinition.custom as ComponentSerializedCustomData

    if (!linkList && !translations?.linkList) {
        return {success: true, description: 'Duplicated text links data items'}
    }

    const repeatedComponents = pointers.structure.getAllDisplayedOnlyComponents(compToAddPointer)

    _.forEach(repeatedComponents, (componentPointer: CompRef) => {
        const repeatedItemId = displayedOnlyStructureUtil.getRepeaterItemId(componentPointer.id)
        const linkListKey = repeatedItemId ? repeatedItemId : originalCompId
        const repeatedData = repeatedItemId
            ? componentDefinition.data.overrides[repeatedItemId]
            : componentDefinition.data
        if (linkList?.[linkListKey]) {
            const newLinks = deepClone(linkList[linkListKey])
            const text = repeatedData.richText?.text ?? repeatedData.text
            createTextLinkReferences(createExtArgs, componentPointer, newLinks, text)
        }

        if (translations?.linkList && componentDefinition.translations) {
            Object.entries(translations.linkList as LinkList).forEach(([languageCode, translatedLinkList]) => {
                if (!translatedLinkList?.[linkListKey]?.length) {
                    return
                }
                const newLinks = deepClone(translatedLinkList[linkListKey])
                let translatedText
                if (repeatedItemId) {
                    translatedText = componentDefinition.translations[languageCode].overrides[repeatedItemId].text
                } else {
                    translatedText =
                        componentDefinition.translations[languageCode].richText?.text ??
                        componentDefinition.translations[languageCode].text
                }

                createTextLinkReferences(createExtArgs, componentPointer, newLinks, translatedText, languageCode)
            })
        }
    })

    return {success: true, description: 'Duplicated text links data items'}
}
